import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react'
import { useRouter } from 'next/router'
import { useSelector } from 'react-redux'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { cn } from '@/lib/utils'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@components/ui/tabs'
import GlowingInput from '@features/home/GlowingInput'

const tabs = [
  { key: 'models', label: 'Try AIs' },
  { key: 'media', label: 'Media' },
  { key: 'news', label: 'News + AI' },
  { key: 'developer', label: 'Developers' },
]

const tabComponents = {
  models: dynamic(() => import('components/ModelsComponent/ModelsComponent')),
  media: dynamic(() => import('components/MediaComponent/MediaComponent')),
  news: dynamic(() => import('components/NewsComponent/NewsComponent')),
  developer: dynamic(() => import('features/home/DeveloperTab')),
}

const jsonLdData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      name: 'Styley',
      url: 'https://styley.ai/',
      logo: 'https://styley.ai/assets/common/StyleyLogo.png',
    },
    {
      '@type': 'WebSite',
      url: 'https://styley.ai/',
      name: 'Styley',
    },
  ],
}

export default function Landing() {
  const tabsRef = useRef()
  const router = useRouter()
  const { tab: tabValue, inputType: defaultInputType } = router.query
  const inputType = defaultInputType || 'styley-text-to-image'

  const user = useSelector((state) => state.auth.user)
  const { activeTheme } = useSelector((state) => state.theme)

  const [activeTab, setActiveTab] = useState(tabValue || 'models')
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })

  const isSharpTheme = useMemo(() => activeTheme?.includes('sharp-theme'), [activeTheme])

  useEffect(() => {
    if (router?.query?.tab) {
      setActiveTab(router.query.tab)
    }
  }, [router?.query?.tab])

  useEffect(() => {
    if (isSharpTheme) {
      const handleMouseMove = (e) => {
        setMousePosition({ x: e.clientX, y: e.clientY })
      }

      window.addEventListener('mousemove', handleMouseMove)
      return () => window.removeEventListener('mousemove', handleMouseMove)
    }
  }, [isSharpTheme])

  const handleTabChange = useCallback(
    (value) => {
      router.push({ pathname: `/`, query: { tab: value } }, undefined, { shallow: true })
      setActiveTab(value)
    },
    [router]
  )

  return (
    <div className="landing w-full flex flex-col items-center">
      <Head>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdData) }} />
      </Head>
      <section
        className={cn('relative w-full', isSharpTheme ? 'text-white' : 'text-black', 'mb-0 xl:mb-[-10px] md:mb-[20px]')}
      >
        <div
          className={cn(
            'min-h-[40vh] xl:min-h-[35vh] flex flex-col items-center justify-center w-full px-[3%] md:px-5',
            isSharpTheme && 'relative overflow-hidden'
          )}
        >
          {isSharpTheme && (
            <div className="absolute inset-0 bg-black">
              <div className="absolute inset-0 opacity-30 bg-grid-white/[0.2] bg-[length:50px_50px]" />
              <div className="absolute top-[-20%] left-[-10%] w-[300px] h-[300px] md:w-[500px] md:h-[500px] rounded-full bg-gradient-to-r from-purple-700/40 to-fuchsia-700/40 blur-3xl animate-blob" />
              <div className="absolute bottom-[-20%] right-[-10%] w-[350px] h-[350px] md:w-[600px] md:h-[600px] rounded-full bg-gradient-to-r from-cyan-700/40 to-blue-700/40 blur-3xl animate-blob animation-delay-2000" />
              <div className="absolute top-[40%] right-[20%] w-[250px] h-[250px] md:w-[400px] md:h-[400px] rounded-full bg-gradient-to-r from-pink-700/40 to-rose-700/40 blur-3xl animate-blob animation-delay-4000" />

              <div
                className="absolute w-[150px] h-[150px] md:w-[200px] md:h-[200px] rounded-full bg-cyan-500/30 blur-3xl pointer-events-none transition-all duration-300"
                style={{
                  left: `${mousePosition.x - 100}px`,
                  top: `${mousePosition.y - 100}px`,
                  opacity: 0.2,
                }}
              />
            </div>
          )}

          <div className="w-full max-w-6xl mx-auto relative z-10">
            <div className="text-center space-y-4 md:space-y-6 xl:space-y-4 mb-3 px-4">
              <h1
                className={cn(
                  'text-5xl md:text-6xl xl:text-5xl mt-10 xl:mt-6 font-bold tracking-tight',
                  isSharpTheme ? 'text-white' : 'text-black'
                )}
              >
                <>
                  <span className={isSharpTheme ? 'text-shadow-glow' : ''}>Build .</span>{' '}
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#E61D2B] to-[#8F00FF] animate-pulse">
                    Generate
                  </span>{' '}
                  <span className={isSharpTheme ? 'text-shadow-glow' : ''}>. Amaze</span>
                </>
              </h1>
              <p
                className={cn(
                  'text-base md:text-lg max-w-2xl mx-auto',
                  isSharpTheme ? 'text-cyan-200' : 'text-gray-500'
                )}
              >
                {isSharpTheme && <span className="inline-block animate-pulse-slow mr-1">✨</span>}
                Create stunning content with AI in seconds
                {isSharpTheme && <span className="inline-block animate-pulse-slow ml-1">✨</span>}
              </p>
            </div>

            <GlowingInput isSharpTheme={isSharpTheme} initialInputType={inputType} />
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full mt-5 ">
          <TabsList
            ref={tabsRef}
            className="py-1 xl:py-0.5 min-h-max gap-2 md:gap-3 xl:gap-4 bg-transparent my-4 md:my-2 xl:my-0 px-2 md:px-16 w-full flex justify-center overflow-x-auto sm:overflow-visible"
            id="mediaModel"
          >
            {tabs.map((tab) => (
              <TabsTrigger
                key={`tab-${tab.key}`}
                value={tab.key}
                className={cn(
                  'py-1.5  md:py-2 text-sm  px-3 md:px-4 xl:px-3 md:w-[130px] xl:w-[110px] whitespace-nowrap border font-bold text-[#5E5E5E]',
                  isSharpTheme ? '!rounded-none !bg-white' : '!rounded-full !bg-white',
                  isSharpTheme
                    ? 'data-[state=active]:!text-white data-[state=active]:!bg-black data-[state=active]:border-2 xl:data-[state=active]:border-2 data-[state=active]:border-solid data-[state=active]:border-black data-[state=active]:rounded-none'
                    : 'data-[state=active]:!text-white data-[state=active]:!bg-black data-[state=active]:border-2 xl:data-[state=active]:border-2 data-[state=active]:border-solid data-[state=active]:border-black data-[state=active]:rounded-full',
                  'transition-all duration-200 hover:bg-gray-50'
                )}
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="px-2 xl:px-8 md:px-6 w-full">
            {tabs.map((tab) => {
              const Component = tabComponents[tab.key]
              return (
                <TabsContent key={tab.key} value={tab.key}>
                  {activeTab === tab.key && (
                    <Component {...(tab.key === 'developer' ? { user } : {})} key={`${tab.key}-component`} />
                  )}
                </TabsContent>
              )
            })}
          </div>
        </Tabs>
      </section>
    </div>
  )
}

export const getStaticProps = async ({ locale }) => {
  try {
    return {
      props: {
        ...(await serverSideTranslations(locale ?? 'en', ['common'])),
      },
      revalidate: 60,
    }
  } catch (error) {
    console.error('Translation loading error:', error)
    return {
      props: {
        _nextI18Next: {
          initialI18nStore: {
            [locale ?? 'en']: { common: {} },
          },
          initialLocale: locale ?? 'en',
          ns: ['common'],
          userConfig: null,
        },
      },
      revalidate: 60,
    }
  }
}
